"use client";

import React from "react";
import Link from "next/link";

interface GlassButtonProps {
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
  children?: React.ReactNode;
  className?: string;
  size?: "sm" | "md" | "lg";
  style?: React.CSSProperties;
  ariaLabel?: string;
}

export function GlassButton({ href, onClick, children, className = "", size = "md", style, ariaLabel }: GlassButtonProps) {
  const padding = size === "sm" ? "px-4 py-2 text-sm" : size === "lg" ? "px-8 py-4 text-lg" : "px-6 py-3 text-base";

  const baseStyle: React.CSSProperties = {
    background: "linear-gradient(180deg, rgba(255,255,255,0.92), rgba(255,255,255,0.86))",
    border: "1px solid rgba(255,255,255,0.6)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    boxShadow: "0 8px 30px -12px rgba(2,6,23,0.18), inset 0 1px 0 rgba(255,255,255,0.6)",
  };

  const content = (
    <span
      className={`inline-flex items-center justify-center rounded-full font-semibold ${padding} ${className}`}
      style={{ color: "rgba(17,24,39,0.95)", ...baseStyle, ...(style || {}) }}
      aria-label={ariaLabel}
    >
      <span className="relative overflow-hidden">
        {/* subtle diagonal shine */}
        <span
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "linear-gradient(120deg, rgba(255,255,255,0.0) 0%, rgba(255,255,255,0.55) 40%, rgba(255,255,255,0.0) 70%)",
            transform: "translateX(-110%) rotate(-6deg)",
            transition: "transform 650ms cubic-bezier(.2,.9,.2,1)",
          }}
        />
        <span className="relative z-10 flex items-center gap-3">
          {children}
        </span>
      </span>
    </span>
  );

  if (href) {
    return (
      <Link href={href} onClick={onClick} className="inline-block hover:scale-[1.02] transition-transform duration-200">
        {content}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className="inline-block hover:scale-[1.02] transition-transform duration-200">
      {content}
    </button>
  );
}

export default GlassButton;
